/**
 * reorderRowsOrColumns.ts - Complete a drag-to-reorder of rows or columns.
 *
 * This method has no single pure `Table -> Table` transform; it orchestrates the full
 * `onDragComplete` flow (validation, frozen-column adjustment, the reorder op, selection update, and
 * analytics). Its reusable pure part is `computeFrozenCountAfterReorder`.
 */

import {range} from '@benchling/js-utils';
import produce from 'immer';

import {assert} from 'Asserts';
import * as benchlingUtil from 'BenchlingUtil';
import {TableDimension, TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {computeFrozenCountAfterReorder} from 'notebook/tables/core-ui/FreezeColumnUtils';
import {ReorderManualOptions} from 'notebook/tables/excess/ops/TableOps';
import {colNumberToLetters, rowIndexToNumber} from 'notebook/tables/excess/Util';
import type {TableMutationParams} from 'notebook/tables/store/logic/mutations/mutationParams';
import {saveSelection, setMultiIndices} from 'notebook/tables/store/logic/mutations/selection';
import TableUtil from 'notebook/tables/TableUtil';
import {trackDragToReorderRowsOrColumns} from 'notebook/util/analytics/TableAnalyticsUtils';
import notifications from 'notifications';

/**
 * Complete a drag-to-reorder of rows or columns.
 *
 * Reads the in-progress drag dimension and multi-selection from the model, clears the dragging
 * state, rejects (surfacing an error notification) when the drop position would split a merged cell,
 * checkpoints the selection for undo via `saveSelection`, adjusts the frozen-column count for column
 * reorders, performs the reorder via the `reorderSingleInputs` op (skipped when the drop is a no-op),
 * updates the selection to the reordered range via `setMultiIndices`, and tracks analytics.
 *
 * Does NOT move DOM focus — the caller is responsible for focusing the focus-bearer element
 * afterward.
 *
 * @param params - The shared mutation dependencies.
 * @param insertIndex - The index the dragged range is dropped before.
 */
export function reorderRowsOrColumns(params: TableMutationParams, insertIndex: number): void {
  const model = params.getTableModel();
  const {draggingDimension, multiStartIndex, multiEndIndex, multiSelIndex} = model.state;
  assert(draggingDimension != null, 'Expected to have been dragging');
  assert(multiStartIndex != null && multiEndIndex != null, 'Expected to have a selection');

  // Dragging is complete; clear the in-progress drag dimension.
  params.setState({draggingDimension: null});

  const {table, index, fileId, computedTable, isMaximized, isSplitScreen, updateHandler} = model.props;
  // `effectiveSlices` (start/end) is the visible column range; `boundaryIndex ?? 0` is
  // the single-input boundary used for the frozen-column computation.
  const {start: sliceStart, end: sliceEnd} = model.singleInputs.effectiveSlices;
  const siBoundary = model.singleInputs.boundaryIndex ?? 0;

  // Need to sort since start > end when you drag from the bottom to the top.
  const dimIdx = draggingDimension === TableDimension.ROW ? 0 : 1;
  const [selectionStart, selectionEnd] = [multiStartIndex[dimIdx], multiEndIndex[dimIdx]].sort(
    (a, b) => a - b
  );
  const numMoved = selectionEnd - selectionStart + 1;

  // Error if the insert index would split a partial merged cell.
  const partialMergedCellInInsertRegion = TableUtil.hasMergedCellCrossingLine(
    computedTable,
    draggingDimension,
    insertIndex,
    {
      // if the table is sliced, ignore columns that aren't shown
      ignoredColumnIndices: draggingDimension === TableDimension.ROW ? range(0, sliceStart - 1) : undefined,
    }
  );
  if (partialMergedCellInInsertRegion) {
    const dimensionName = draggingDimension === TableDimension.ROW ? 'row' : 'column';
    const indexToReadableName =
      draggingDimension === TableDimension.ROW ? rowIndexToNumber : colNumberToLetters;
    const mergedCellPosition = partialMergedCellInInsertRegion
      .map((idx) => indexToReadableName(idx))
      .join(' to ');
    notifications.error(
      `Cannot move ${benchlingUtil.pluralize(numMoved, dimensionName)} to position ${indexToReadableName(insertIndex)} since it crosses a merged cell in ${dimensionName}s ${mergedCellPosition}. Please unmerge the cell and try again.`,
      {id: 'drag-to-reorder-partial-merged-cell'}
    );
    return;
  }

  // Save selection to history before making the change.
  saveSelection(params, {multiStartIndex, multiEndIndex, multiSelIndex});

  const reorderOptions: ReorderManualOptions = {
    startIndex: selectionStart,
    endIndex: selectionEnd,
    insertPosition: insertIndex,
    dim: draggingDimension,
  };

  // Update frozenColumnCount before the reorder mutation so the reorder sees the
  // correct frozen state. These are two dispatches, not a single atomic operation.
  if (draggingDimension === TableDimension.COLUMN) {
    const newFrozenCount = computeFrozenCountAfterReorder({
      selectionStart,
      selectionEnd,
      insertIndex,
      siBoundary,
      effectiveFrozen: TableUtil.getAffixedColumnCount(table),
      absoluteFreezeBoundary: TableUtil.getFreezeBoundary(table),
    });
    if (newFrozenCount != null) {
      const newTable = produce(table, (draft) => {
        draft.frozenColumnCount = newFrozenCount;
      });
      updateHandler(index, {table: newTable});
    }
  }

  // If the user dragged but didn't reorder, we can skip calling the actual reorder op.
  if (insertIndex !== selectionStart) {
    params.tableOps.reorderTableRowsOrColumns(table.name, reorderOptions);
  }

  // Set selection to the reordered range. `_rowRange`/`_colRange` geometry is
  // inlined here: rows span the effective slice columns, columns span all rows.
  const newFirstIndex = insertIndex > selectionStart ? insertIndex - numMoved : insertIndex;
  const newLastIndex = newFirstIndex + numMoved - 1;
  const lastRowIndex = table.table.length - 1;
  const newSelection =
    draggingDimension === TableDimension.ROW
      ? {
          start: [newFirstIndex, sliceStart] as TableIndex,
          end: [newLastIndex, sliceEnd] as TableIndex,
        }
      : {start: [0, newFirstIndex] as TableIndex, end: [lastRowIndex, newLastIndex] as TableIndex};
  setMultiIndices(params, newSelection);

  trackDragToReorderRowsOrColumns(
    table,
    reorderOptions,
    {fileId},
    {
      isMaximized: isMaximized ?? false,
      isFixedHeight: true,
      isSplitScreen: isSplitScreen ?? false,
    }
  );
}
